import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaTimes } from 'react-icons/fa';
import { 
  getInvaiteUsersEvent, 
  getEventAttendees,
  getVolunteers 
} from '../../../../Redux/Reducers/communityReducer';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PeopleIcon from '@mui/icons-material/People';

const StatisticsModal = ({ isOpen, onClose, statisticLabel, statisticData, eventId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  
  // Get data from Redux store
  const invitedUsers = useSelector((state) => state.community.getInviteUserEvent);
  const eventAttendees = useSelector((state) => state.community.EventAttendees);
  const volunteers = useSelector((state) => state.community.VolunteersData);

  useEffect(() => {
    if (isOpen && eventId) {
      fetchStatisticData();
    }
  }, [isOpen, statisticLabel, eventId]);

  const fetchStatisticData = async () => {
    setLoading(true);
    try {
      switch (statisticLabel) {
        case "Invited":
          await dispatch(getInvaiteUsersEvent({ id: eventId, page: 0 }));
          break;
        case "Total Attendees":
        case "Joined":
        case "Pending":
        case "Rejected":
        case "Cancelled":
          await dispatch(getEventAttendees(eventId));
          break;
        case "Total Volunteers":
          await dispatch(getVolunteers(eventId));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${statisticLabel} data:`, error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Filter attendees based on status
  const getFilteredAttendees = () => {
    if (!eventAttendees) return [];
    
    switch (statisticLabel) {
      case "Joined":
        return eventAttendees.filter(a => a.attendance_status === "APPROVED");
      case "Pending":
        return eventAttendees.filter(a => a.attendance_status === "PENDING");
      case "Rejected":
        return eventAttendees.filter(a => a.attendance_status === "REJECTED");
      case "Cancelled":
        return eventAttendees.filter(a => a.attendance_status === "CANCELLED");
      default:
        return eventAttendees;
    }
  };

  // Get the appropriate data based on statistic label
  const getDisplayData = () => {
    switch (statisticLabel) {
      case "Invited":
        return invitedUsers || [];
      case "Total Volunteers":
        return volunteers || [];
      case "Total Attendees":
      case "Joined":
      case "Pending":
      case "Rejected":
      case "Cancelled":
        return getFilteredAttendees();
      default:
        return [];
    }
  };

  const displayData = getDisplayData();
  const title = statisticLabel || "Details";
  const totalCount = displayData.length;

  // Get icon based on statistic type
  const getIcon = () => {
    switch (statisticLabel) {
      case "Invited":
        return <GroupAddIcon sx={{ fontSize: 60, color: '#0044C6', mb: 2 }} />;
      case "Total Volunteers":
        return <PeopleIcon sx={{ fontSize: 60, color: '#0044C6', mb: 2 }} />;
      default:
        return <PeopleIcon sx={{ fontSize: 60, color: '#0044C6', mb: 2 }} />;
    }
  };

  // Render the list items based on data type
  const renderListItem = (item, index) => {
    if (statisticLabel === "Invited") {
      // Render invited users
      return (
        <React.Fragment key={item.id}>
          <ListItem>
            <ListItemAvatar>
              <Avatar src={item.profile_url}>
                {item.first_name?.charAt(0)}{item.last_name?.charAt(0)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`${item.first_name} ${item.last_name || ''}`}
              secondary={
                <>
                  {item.designation && <span>{item.designation}</span>}
                  {item.company_name && (
                    <span>{item.designation ? ', ' : ''}{item.company_name}</span>
                  )}
                  {item.created_at && (
                    <Typography component="span" variant="body2" color="text.secondary" display="block">
                      Invited: {new Date(item.created_at).toLocaleDateString()}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
          {index < displayData.length - 1 && <Divider />}
        </React.Fragment>
      );
    } else if (statisticLabel === "Total Volunteers") {
      // Render volunteers
      return (
        <React.Fragment key={item.id || index}>
          <ListItem>
            <ListItemAvatar>
              <Avatar src={item.profile}>
                {item.name?.charAt(0)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={item.name || "Unknown"}
              secondary={item.email || "Email not available"}
            />
          </ListItem>
          {index < displayData.length - 1 && <Divider />}
        </React.Fragment>
      );
    } else {
      // Render attendees
      const attendee = item.attendee || {};
      return (
        <React.Fragment key={item.id || index}>
          <ListItem>
            <ListItemAvatar>
              <Avatar src={attendee.profile_url}>
                {attendee.name ? attendee.name.charAt(0) : "?"}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={attendee.name || "Unknown"}
              secondary={
                <>
                  <span>{attendee.email || "Email not available"}</span>
                  {item.attendance_status && (
                    <Typography 
                      component="span" 
                      variant="body2" 
                      color={
                        item.attendance_status === "APPROVED" ? "success.main" :
                        item.attendance_status === "REJECTED" ? "error.main" :
                        item.attendance_status === "PENDING" ? "warning.main" :
                        "text.secondary"
                      }
                      display="block"
                    >
                      Status: {item.attendance_status}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
          {index < displayData.length - 1 && <Divider />}
        </React.Fragment>
      );
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : displayData.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight={200}
          >
            {getIcon()}
            <Typography variant="body1" color="textSecondary">
              No {title.toLowerCase()} found
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Total count: {totalCount}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2, px: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Total {title.toLowerCase()}: <strong>{totalCount}</strong>
              </Typography>
            </Box>
            <List>
              {displayData.map((item, index) => renderListItem(item, index))}
            </List>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatisticsModal;